import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/MarketHolidays";

const EXCHANGES = ["NSE", "BSE", "MCX", "NFO", "BFO", "CDS", "BOTH"];

const newRow = () => ({
  _key: crypto.randomUUID(),
  date: "",
  exchange: "NSE",
  reason: "",
});

function AddMarketHoliday() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([newRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");

  // ── Row helpers ───────────────────────────────────────────────────────────
  const setRowField = (key, field, val) =>
    setRows((p) =>
      p.map((r) => (r._key === key ? { ...r, [field]: val } : r))
    );

  const addRow = () => setRows((p) => [...p, newRow()]);

  const removeRow = (key) =>
    setRows((p) => p.filter((r) => r._key !== key));

  // ── Bulk paste parse ──────────────────────────────────────────────────────
  // Expected format per line: YYYY-MM-DD,EXCHANGE,Reason
  // e.g. 2026-03-06,NSE,Holi
  const parseBulk = () => {
    const lines = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    if (!lines.length) return;

    const parsed = lines.map((line) => {
      const parts = line.split(",").map((p) => p.trim());
      return {
        _key: crypto.randomUUID(),
        date: parts[0] ?? "",
        exchange: parts[1]?.toUpperCase() ?? "NSE",
        reason: parts[2] ?? "",
      };
    });

    setRows(parsed);
    setBulkMode(false);
    setBulkText("");
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!e.target.checkValidity()) {
      e.target.classList.add("was-validated");
      return;
    }

    const validRows = rows.filter((r) => r.date.trim() && r.exchange.trim());
    if (!validRows.length) {
      await Swal.fire({
        title: "No Entries",
        text: "Please add at least one holiday entry.",
        icon: "info",
        confirmButtonColor: "#0d6efd",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
      return;
    }

    const payload = validRows.map(({ date, exchange, reason }) => ({
      date,
      exchange,
      ...(reason.trim() && { reason: reason.trim() }),
    }));

    console.log("📤 AddMarketHolidays payload:", payload);
    setSubmitting(true);

    try {
      const res = await fetch(`${BASE_URL}/AddMarketHolidays`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("✅ AddMarketHolidays response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      await Swal.fire({
        title: "Holidays Added!",
        text:
          data?.message ??
          `${payload.length} holiday${payload.length > 1 ? "s" : ""} added successfully.`,
        icon: "success",
        confirmButtonColor: "#198754",
        confirmButtonText: "Go to Holidays",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });

      navigate("/manage/market-holidays");
    } catch (err) {
      console.error("❌ AddMarketHolidays error:", err);
      await Swal.fire({
        title: "Failed to Add",
        text: err.message || "Something went wrong.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── UI helpers ────────────────────────────────────────────────────────────
  const SectionHead = ({ icon, title }) => (
    <div className="col-12 mt-4 mb-1">
      <h6 className="fw-semibold text-dark d-flex align-items-center gap-2 mb-0">
        <i className={`bi ${icon} text-primary`}></i> {title}
      </h6>
      <hr className="mt-1 mb-0" />
    </div>
  );

  const exchangeBadgeColor = (ex) => {
    const map = {
      NSE:  "#0d6efd",
      BSE:  "#0dcaf0",
      MCX:  "#ffc107",
      NFO:  "#6f42c1",
      BFO:  "#fd7e14",
      CDS:  "#20c997",
      BOTH: "#198754",
    };
    return map[ex?.toUpperCase()] ?? "#6c757d";
  };

  const filledCount = rows.filter((r) => r.date.trim() && r.exchange.trim()).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pt-1 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div>
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    Add Market Holidays
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Add exchange holidays for NSE / BSE / MCX
                  </p>
                </div>
                <div>
                  <span
                    onClick={() => navigate(-1)}
                    className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-arrow-left me-1"></i> Back
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <form
              className="row g-2 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              <SectionHead icon="bi-calendar-x" title="Holiday Entries" />

              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">

                  {/* Mode toggle */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="small text-secondary fw-semibold me-1">
                      Input Mode:
                    </span>
                    <button
                      type="button"
                      className={`btn btn-sm px-3 rounded-3 ${!bulkMode ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setBulkMode(false)}
                    >
                      <i className="bi bi-ui-checks me-1"></i> Row by Row
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm px-3 rounded-3 ${bulkMode ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setBulkMode(true)}
                    >
                      <i className="bi bi-textarea-t me-1"></i> Bulk Paste
                    </button>
                  </div>

                  {/* ── Bulk Paste Mode ── */}
                  {bulkMode ? (
                    <div className="row g-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label small">
                          Paste Holidays
                          <span className="text-secondary fw-normal ms-2">
                            (one per line: DATE, EXCHANGE, Reason)
                          </span>
                        </label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={8}
                          placeholder={`2026-03-06,NSE,Holi\n2026-03-07,BSE,Holi\n2026-04-14,NSE,Dr. Ambedkar Jayanti`}
                          value={bulkText}
                          onChange={(e) => setBulkText(e.target.value)}
                          style={{ fontFamily: "monospace", fontSize: 13 }}
                        />
                        <div className="form-text">
                          Format: <code>YYYY-MM-DD, EXCHANGE, Reason</code>.
                          Reason is optional.
                        </div>
                      </div>
                      <div className="col-12">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary rounded-3"
                          onClick={parseBulk}
                          disabled={!bulkText.trim()}
                        >
                          <i className="bi bi-arrow-right-circle me-1"></i>
                          Parse &amp; Preview Rows
                        </button>
                      </div>

                      {/* Preview */}
                      {rows.length > 0 && rows[0].date && (
                        <div className="col-12 mt-2">
                          <p className="small text-secondary mb-2">
                            <i className="bi bi-eye me-1"></i>
                            Preview — {rows.length} entr{rows.length > 1 ? "ies" : "y"} parsed:
                          </p>
                          <div className="d-flex flex-column gap-1">
                            {rows.map((r) => (
                              <div
                                key={r._key}
                                className="d-inline-flex align-items-center gap-2 bg-white border rounded-3 px-3 py-2"
                                style={{ maxWidth: 420 }}
                              >
                                <span
                                  className="badge rounded-2 px-2 py-1"
                                  style={{
                                    background: exchangeBadgeColor(r.exchange),
                                    fontSize: 11,
                                    minWidth: 38,
                                  }}
                                >
                                  {r.exchange}
                                </span>
                                <code className="text-secondary small">
                                  {r.date}
                                </code>
                                {r.reason && (
                                  <span className="text-dark small">
                                    {r.reason}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* ── Row by Row Mode ── */
                    <div>
                      {/* Column headers — desktop only */}
                      <div className="row g-2 mb-1 d-none d-md-flex">
                        <div className="col-md-3">
                          <span className="form-label small mb-0">
                            Date <span className="text-danger fw-bold">*</span>
                          </span>
                        </div>
                        <div className="col-md-2">
                          <span className="form-label small mb-0">
                            Exchange <span className="text-danger fw-bold">*</span>
                          </span>
                        </div>
                        <div className="col-md-5">
                          <span className="form-label small mb-0">Reason</span>
                        </div>
                      </div>

                      {rows.map((row, idx) => (
                        <div
                          className="row g-2 mb-2 align-items-center"
                          key={row._key}
                        >
                          {/* Date */}
                          <div className="col-md-3 col-sm-5 col-12">
                            {idx === 0 && (
                              <label className="form-label small d-md-none">
                                Date <span className="text-danger fw-bold">*</span>
                              </label>
                            )}
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={row.date}
                              onChange={(e) =>
                                setRowField(row._key, "date", e.target.value)
                              }
                              required
                            />
                            <div className="invalid-feedback">
                              Please select a date.
                            </div>
                          </div>

                          {/* Exchange */}
                          <div className="col-md-2 col-sm-3 col-6">
                            {idx === 0 && (
                              <label className="form-label small d-md-none">
                                Exchange <span className="text-danger fw-bold">*</span>
                              </label>
                            )}
                            <select
                              className="form-select form-select-sm"
                              value={row.exchange}
                              onChange={(e) =>
                                setRowField(row._key, "exchange", e.target.value)
                              }
                              required
                            >
                              {EXCHANGES.map((ex) => (
                                <option key={ex} value={ex}>
                                  {ex}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Reason */}
                          <div className="col-md-5 col-sm-12 col-12">
                            {idx === 0 && (
                              <label className="form-label small d-md-none">
                                Reason
                              </label>
                            )}
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Holi"
                              value={row.reason}
                              onChange={(e) =>
                                setRowField(row._key, "reason", e.target.value)
                              }
                            />
                          </div>

                          {/* Exchange colour dot */}
                          <div className="col-auto d-none d-md-flex align-items-center">
                            <span
                              className="badge rounded-3 px-2 py-1"
                              style={{
                                background: exchangeBadgeColor(row.exchange),
                                fontSize: 11,
                                minWidth: 40,
                                color: "#fff",
                              }}
                            >
                              {row.exchange}
                            </span>
                          </div>

                          {/* Remove */}
                          <div className="col-auto">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger rounded-circle p-0 ms-1"
                              style={{ width: 28, height: 28, lineHeight: 1 }}
                              onClick={() => removeRow(row._key)}
                              disabled={rows.length === 1}
                              title="Remove row"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        </div>
                      ))}

                      <div className="d-flex align-items-center gap-3 mt-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary rounded-3"
                          onClick={addRow}
                        >
                          <i className="bi bi-plus-lg me-1"></i> Add Row
                        </button>
                        {rows.length > 1 && (
                          <span className="text-secondary small">
                            {filledCount} / {rows.length} filled
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Submit ── */}
              <div className="col-12 mt-4">
                <button
                  className="btn btn-success px-6 fw-normal py-1 fs-5 ls-sm d-inline-flex align-items-center gap-2"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                      Adding…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-calendar-plus"></i>
                      Add{" "}
                      {filledCount > 0
                        ? `${filledCount} Holiday${filledCount > 1 ? "s" : ""}`
                        : "Holidays"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default AddMarketHoliday;
